import React, {
  useState,
} from "react";


const EMPTY_PLAN = {
  name: "",
  description: "",
  pricingType: "FIXED",
  price: "",
  currency: "INR",
  displayOrder: 0,
};


const OfferingPricingPlans = ({
  offeringId,
  plans = [],
  loading = false,
  submitting = false,
  onCreate,
  onUpdate,
  onDelete,
  onRefresh,
}) => {

  const [form, setForm] = useState({
    ...EMPTY_PLAN,
  });

  const [editingId, setEditingId] = useState(null);

  const [showForm, setShowForm] =
    useState(false);

  const [error, setError] =
    useState(null);


  /*
  |--------------------------------------------------------------------------
  | HANDLE CHANGE
  |--------------------------------------------------------------------------
  */

  const handleChange = (event) => {

    const {
      name,
      value,
    } = event.target;


    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };


  /*
  |--------------------------------------------------------------------------
  | RESET FORM
  |--------------------------------------------------------------------------
  */

  const resetForm = () => {

    setForm({
      ...EMPTY_PLAN,
    });

    setEditingId(null);

    setShowForm(false);

    setError(null);
  };


  /*
  |--------------------------------------------------------------------------
  | OPEN CREATE FORM
  |--------------------------------------------------------------------------
  */

  const openCreateForm = () => {

    if (!offeringId) {

      setError(
        "Offering ID is missing. Please reload the offering."
      );

      return;
    }


    setForm({
      ...EMPTY_PLAN,
    });

    setEditingId(null);

    setError(null);

    setShowForm(true);
  };


  /*
  |--------------------------------------------------------------------------
  | START EDIT
  |--------------------------------------------------------------------------
  */

  const startEdit = (plan) => {

    if (!plan?.id) {
      return;
    }


    const pricingType =
      String(
        plan.pricing_type ??
        plan.pricingType ??
        "FIXED"
      ).toUpperCase();


    const displayOrder =
      plan.display_order ??
      plan.displayOrder ??
      0;


    setEditingId(
      plan.id
    );


    setForm({

      name:
        plan.name ??
        "",


      description:
        plan.description ??
        "",


      pricingType:
        pricingType === "CUSTOM"
          ? "CUSTOM"
          : "FIXED",


      price:
        plan.price !== null &&
        plan.price !== undefined
          ? String(plan.price)
          : "",


      currency:
        String(
          plan.currency ??
          "INR"
        )
          .toUpperCase(),


      displayOrder:
        String(displayOrder),
    });


    setError(null);

    setShowForm(true);
  };


  /*
  |--------------------------------------------------------------------------
  | BUILD PAYLOAD
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  |
  | The offering ID belongs in the URL:
  |
  | POST /pricing-plans/offering/:offeringId
  |
  | It should NOT be sent as part of the request body.
  |
  */

  const buildPayload = () => {

    const name =
      String(
        form.name ?? ""
      ).trim();


    if (!name) {

      throw new Error(
        "Plan name is required."
      );
    }


    const pricingType =
      String(
        form.pricingType ??
        "FIXED"
      ).toUpperCase();


    if (
      pricingType !== "FIXED" &&
      pricingType !== "CUSTOM"
    ) {

      throw new Error(
        "Invalid pricing type."
      );
    }


    const currency =
      String(
        form.currency ??
        "INR"
      )
        .trim()
        .toUpperCase();


    if (!/^[A-Z]{3}$/.test(currency)) {

      throw new Error(
        "Currency must contain exactly 3 letters."
      );
    }


    let price = null;


    if (pricingType === "FIXED") {

      if (
        form.price === "" ||
        form.price === null ||
        form.price === undefined
      ) {

        throw new Error(
          "Price is required for fixed pricing."
        );
      }


      const numericPrice =
        Number(form.price);


      if (
        !Number.isFinite(
          numericPrice
        ) ||
        numericPrice < 0
      ) {

        throw new Error(
          "Please enter a valid price."
        );
      }


      price =
        numericPrice;
    }


    const displayOrder =
      Number(
        form.displayOrder
      );


    if (
      !Number.isInteger(
        displayOrder
      ) ||
      displayOrder < 0
    ) {

      throw new Error(
        "Display order must be a valid non-negative number."
      );
    }


    /*
    |--------------------------------------------------------------------------
    | FINAL API PAYLOAD
    |--------------------------------------------------------------------------
    */

    return {

      name,

      description:
        String(
          form.description ?? ""
        ).trim(),

      pricingType,

      price,

      currency,

      displayOrder,
    };
  };


  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (event) => {

    event.preventDefault();

    setError(null);


    /*
    |--------------------------------------------------------------------------
    | OFFERING ID
    |--------------------------------------------------------------------------
    */

    if (!offeringId) {

      setError(
        "Offering ID is required."
      );

      return;
    }


    try {

      /*
      |--------------------------------------------------------------------------
      | CREATE PAYLOAD
      |--------------------------------------------------------------------------
      */

      const payload =
        buildPayload();


      /*
      |--------------------------------------------------------------------------
      | DEBUG
      |--------------------------------------------------------------------------
      |
      | This should show the exact object being sent to the parent.
      |
      */

      console.log(
        "[PRICING PLAN] offeringId:",
        offeringId
      );

      console.log(
        "[PRICING PLAN] payload:",
        payload
      );


      /*
      |--------------------------------------------------------------------------
      | CREATE
      |--------------------------------------------------------------------------
      */

      if (!editingId) {

        if (
          typeof onCreate !==
          "function"
        ) {

          throw new Error(
            "Create pricing plan handler is not available."
          );
        }


        /*
        |--------------------------------------------------------------------------
        | IMPORTANT:
        |
        | Parent receives ONLY payload.
        |
        | Parent adds offeringId to the API URL.
        |--------------------------------------------------------------------------
        */

        await onCreate(
          payload
        );
      }


      /*
      |--------------------------------------------------------------------------
      | UPDATE
      |--------------------------------------------------------------------------
      */

      else {

        if (
          typeof onUpdate !==
          "function"
        ) {

          throw new Error(
            "Update pricing plan handler is not available."
          );
        }


        await onUpdate(
          editingId,
          payload
        );
      }


      /*
      |--------------------------------------------------------------------------
      | REFRESH
      |--------------------------------------------------------------------------
      */

      if (
        typeof onRefresh ===
        "function"
      ) {

        await onRefresh();
      }


      /*
      |--------------------------------------------------------------------------
      | RESET
      |--------------------------------------------------------------------------
      */

      resetForm();

    } catch (err) {

      console.error(
        "Failed to save pricing plan:",
        err
      );


      /*
      |--------------------------------------------------------------------------
      | AUTH ERROR
      |--------------------------------------------------------------------------
      */

      if (
        err?.response?.status === 401
      ) {

        setError(
          "Authentication failed. Your login token is invalid or expired. Please log in again."
        );

        return;
      }


      /*
      |--------------------------------------------------------------------------
      | BACKEND ERROR
      |--------------------------------------------------------------------------
      */

      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save pricing plan."
      );
    }
  };


  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  */

  const handleDelete = async (plan) => {

    if (!plan?.id) {
      return;
    }


    const confirmed =
      window.confirm(
        `Delete pricing plan "${plan.name}"?`
      );


    if (!confirmed) {
      return;
    }


    try {

      setError(null);


      if (
        typeof onDelete !==
        "function"
      ) {

        throw new Error(
          "Delete pricing plan handler is not available."
        );
      }


      await onDelete(
        plan
      );


      if (
        typeof onRefresh ===
        "function"
      ) {

        await onRefresh();
      }

    } catch (err) {

      console.error(
        "Failed to delete pricing plan:",
        err
      );


      if (
        err?.response?.status === 401
      ) {

        setError(
          "Authentication failed. Your login token is invalid or expired. Please log in again."
        );

        return;
      }


      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to delete pricing plan."
      );
    }
  };


  /*
  |--------------------------------------------------------------------------
  | TOGGLE STATUS
  |--------------------------------------------------------------------------
  */

  const toggleStatus = async (plan) => {

    if (!plan?.id) {
      return;
    }


    const currentStatus =
      plan.is_active !== undefined
        ? plan.is_active
        : plan.isActive;


    try {

      setError(null);


      if (
        typeof onUpdate !==
        "function"
      ) {

        throw new Error(
          "Update pricing plan handler is not available."
        );
      }


      await onUpdate(
        plan.id,
        {
          isActive:
            !currentStatus,
        }
      );


      if (
        typeof onRefresh ===
        "function"
      ) {

        await onRefresh();
      }

    } catch (err) {

      console.error(
        "Failed to update pricing plan:",
        err
      );


      if (
        err?.response?.status === 401
      ) {

        setError(
          "Authentication failed. Your login token is invalid or expired. Please log in again."
        );

        return;
      }


      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to update pricing plan."
      );
    }
  };


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {

    return (

      <section className="offering-pricing">

        <div className="section-header">

          <div>

            <h3>
              Pricing Plans
            </h3>

            <span>
              Loading...
            </span>

          </div>

        </div>


        <div className="empty-state">
          Loading pricing plans...
        </div>

      </section>
    );
  }


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (

    <section className="offering-pricing">


      {/* HEADER */}

      <div className="section-header">

        <div>

          <h3>
            Pricing Plans
          </h3>

          <span>

            {plans.length} plan
            {plans.length !== 1
              ? "s"
              : ""}

          </span>

        </div>


        {!showForm && (

          <button
            type="button"
            onClick={openCreateForm}
            disabled={
              submitting ||
              !offeringId
            }
          >
            Add Pricing Plan
          </button>

        )}


        {showForm && (

          <button
            type="button"
            onClick={resetForm}
            disabled={submitting}
          >
            Cancel
          </button>

        )}

      </div>


      {/* ERROR */}

      {error && (

        <div className="error-message">

          {error}

        </div>

      )}


      {/* FORM */}

      {showForm && (

        <form
          className="pricing-plan-form"
          onSubmit={handleSubmit}
        >


          {/* NAME */}

          <div className="form-group">

            <label htmlFor="pricing-plan-name">
              Plan Name *
            </label>


            <input
              id="pricing-plan-name"
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. CRM Pro Plan"
              required
              disabled={submitting}
              autoComplete="off"
            />

          </div>


          {/* DESCRIPTION */}

          <div className="form-group">

            <label htmlFor="pricing-plan-description">
              Description
            </label>


            <textarea
              id="pricing-plan-description"
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder="Professional CRM software package"
              disabled={submitting}
            />

          </div>


          {/* TYPE + CURRENCY */}

          <div className="form-row">


            <div className="form-group">

              <label htmlFor="pricing-plan-type">
                Pricing Type *
              </label>


              <select
                id="pricing-plan-type"
                name="pricingType"
                value={form.pricingType}
                onChange={handleChange}
                disabled={submitting}
              >

                <option value="FIXED">
                  Fixed
                </option>

                <option value="CUSTOM">
                  Custom
                </option>

              </select>

            </div>


            <div className="form-group">

              <label htmlFor="pricing-plan-currency">
                Currency *
              </label>


              <input
                id="pricing-plan-currency"
                type="text"
                name="currency"
                maxLength={3}
                value={form.currency}
                onChange={handleChange}
                placeholder="INR"
                required
                disabled={submitting}
              />

            </div>

          </div>


          {/* PRICE */}

          {form.pricingType ===
            "FIXED" && (

            <div className="form-group">

              <label htmlFor="pricing-plan-price">
                Price *
              </label>


              <input
                id="pricing-plan-price"
                type="number"
                min="0"
                step="0.01"
                name="price"
                value={form.price}
                onChange={handleChange}
                placeholder="7399"
                required
                disabled={submitting}
              />

            </div>

          )}


          {/* CUSTOM PRICE */}

          {form.pricingType ===
            "CUSTOM" && (

            <div className="custom-price-info">
              Customer-specific pricing
            </div>

          )}


          {/* DISPLAY ORDER */}

          <div className="form-group">

            <label htmlFor="pricing-plan-order">
              Display Order
            </label>


            <input
              id="pricing-plan-order"
              type="number"
              min="0"
              step="1"
              name="displayOrder"
              value={form.displayOrder}
              onChange={handleChange}
              disabled={submitting}
            />

          </div>


          {/* ACTIONS */}

          <div className="pricing-form-actions">


            <button
              type="button"
              onClick={resetForm}
              disabled={submitting}
            >
              Cancel
            </button>


            <button
              type="submit"
              disabled={
                submitting ||
                !offeringId ||
                !form.name.trim() ||
                (
                  form.pricingType ===
                    "FIXED" &&
                  (
                    form.price === "" ||
                    !Number.isFinite(
                      Number(form.price)
                    ) ||
                    Number(form.price) < 0
                  )
                )
              }
            >

              {submitting
                ? "Saving..."
                : editingId
                  ? "Update Plan"
                  : "Create Plan"}

            </button>

          </div>

        </form>

      )}


      {/* EMPTY */}

      {!plans.length &&
        !showForm && (

        <div className="empty-state">
          No pricing plans added.
        </div>

      )}


      {/* LIST */}

      {plans.length > 0 && (

        <div className="pricing-plans-list">

          {plans.map((plan) => {

            const pricingType =
              String(
                plan.pricing_type ??
                plan.pricingType ??
                "FIXED"
              ).toUpperCase();


            const isCustom =
              pricingType === "CUSTOM";


            const isActive =
              plan.is_active !== undefined
                ? plan.is_active
                : plan.isActive;


            const displayOrder =
              plan.display_order !== undefined
                ? plan.display_order
                : plan.displayOrder;


            const formattedPrice =
              plan.price !== null &&
              plan.price !== undefined
                ? Number(
                    plan.price
                  ).toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    }
                  )
                : null;


            return (

              <div
                className="pricing-plan-card"
                key={plan.id}
              >


                {/* HEADER */}

                <div className="pricing-plan-header">

                  <div>

                    <h4>
                      {plan.name}
                    </h4>


                    <span
                      className={
                        `pricing-type ${
                          isCustom
                            ? "custom"
                            : "fixed"
                        }`
                      }
                    >
                      {isCustom
                        ? "Custom"
                        : "Fixed"}
                    </span>

                  </div>


                  <span
                    className={
                      `plan-status ${
                        isActive
                          ? "active"
                          : "inactive"
                      }`
                    }
                  >
                    {isActive
                      ? "Active"
                      : "Inactive"}
                  </span>

                </div>


                {/* DESCRIPTION */}

                {plan.description && (

                  <p>
                    {plan.description}
                  </p>

                )}


                {/* PRICE */}

                <div className="pricing-plan-price">

                  {isCustom ? (

                    <strong>
                      Contact Us
                    </strong>

                  ) : (

                    <strong>

                      {plan.currency ||
                        "INR"}{" "}

                      {formattedPrice ??
                        "0"}

                    </strong>

                  )}

                </div>


                {/* META */}

                <div className="pricing-plan-meta">

                  <span>
                    Order:{" "}
                    {displayOrder ??
                      0}
                  </span>

                </div>


                {/* ACTIONS */}

                <div className="pricing-plan-actions">


                  <button
                    type="button"
                    onClick={() =>
                      startEdit(plan)
                    }
                    disabled={submitting}
                  >
                    Edit
                  </button>


                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      handleDelete(plan)
                    }
                    disabled={submitting}
                  >
                    Delete
                  </button>


                  <button
                    type="button"
                    onClick={() =>
                      toggleStatus(plan)
                    }
                    disabled={submitting}
                  >
                    {isActive
                      ? "Deactivate"
                      : "Activate"}
                  </button>

                </div>

              </div>

            );
          })}

        </div>

      )}

    </section>
  );
};


export default OfferingPricingPlans;